import React, { useEffect, useState, useCallback } from "react";
import {
  Row,
  Col,
  Table,
  Button,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  message,
  Breadcrumb,
} from "antd";
import {
  EllipsisOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import theme from "../theme";
import { listRoles, createRole, updateRole, deleteRole } from "../services/roles";
import { getIndustry } from "../services/industries";

interface RoleRow {
  id: string;
  name: string;
  created_at: string;
}

interface Industry {
  id: number;
  name: string;
  color_hex: string;
}

const IndustryRoles: React.FC = () => {
  const { industryId } = useParams();
  const navigate = useNavigate();

  const [industry, setIndustry] = useState<Industry | null>(null);
  const [rows, setRows] = useState<RoleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<RoleRow | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!industryId) return;
    fetchData();
  }, [industryId]);

  const fetchData = async () => {
    if (!industryId) return;
    setLoading(true);
    try {
      const [industryData, rolesData] = await Promise.all([
        getIndustry(Number(industryId)),
        listRoles(Number(industryId)),
      ]);
      setIndustry(industryData);
      setRows(
        rolesData.map((r: any) => ({
          id: String(r.id),
          name: r.name,
          created_at: r.created_at,
        }))
      );
    } catch (error) {
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Role Name", dataIndex: "name", key: "name" },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: "actions",
      width: 60,
      render: (_: unknown, row: RoleRow) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item onClick={() => handleEdit(row)}>
                Edit
              </Menu.Item>
              <Menu.Item danger onClick={() => setToDelete(row)}>
                Delete
              </Menu.Item>
            </Menu>
          }
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingRole(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (roleRow: RoleRow) => {
    setEditingRole(roleRow);
    form.setFieldsValue({ name: roleRow.name });
    setModalOpen(true);
  };

  const handleSave = async (values: { name: string }) => {
    if (!industryId) return;

    setSaving(true);
    try {
      if (editingRole) {
        await updateRole(Number(editingRole.id), { name: values.name });
        message.success("Role updated successfully");
      } else {
        await createRole({
          industry: Number(industryId),
          name: values.name,
        });
        message.success("Role created successfully");
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      message.error("Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;

    setDeleting(true);
    try {
      await deleteRole(Number(toDelete.id));
      message.success("Role deleted successfully");
      setToDelete(null);
      fetchData();
    } catch (error) {
      message.error("Failed to delete role");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={bg}>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <Button
            type="link"
            onClick={() => navigate("/industries-list")}
            style={{ padding: 0, height: 'auto' }}
          >
            Industries
          </Button>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Button
            type="link"
            onClick={() => navigate(`/view-industry/${industryId}`)}
            style={{ padding: 0, height: 'auto' }}
          >
            {industry?.name || "Loading..."}
          </Button>
        </Breadcrumb.Item>
      </Breadcrumb>

      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <h1 style={heading}>Roles</h1>
        </Col>

        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={createBtn}
            onClick={handleAdd}
          >
            Add new
          </Button>
        </Col>
      </Row>

      <div style={card}>
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          pagination={false}
          bordered
          locale={{
            emptyText: (
              <div style={emptyStateContainer}>
                <div style={emptyStateText}>No roles found</div>
                <div style={emptyStateSubtext}>Create your first role to get started</div>
              </div>
            )
          }}
        />
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={modalOpen}
        title={editingRole ? "Edit Role" : "Add New Role"}
        okText="Save"
        okButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
        onOk={() => form.submit()}
        onCancel={() => setModalOpen(false)}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: "Please enter role name" }]}
          >
            <Input placeholder="Enter role name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal
        open={!!toDelete}
        title="Delete Role?"
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleting }}
        cancelButtonProps={{ disabled: deleting }}
        onOk={handleDelete}
        onCancel={() => setToDelete(null)}
      >
        Are you sure you want to delete "{toDelete?.name}"?
      </Modal>
    </div>
  );
};

const bg: React.CSSProperties = {
  minHeight: "100vh",
};

const heading: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 700,
  color: theme.colors.primary,
  margin: 0,
};



const card: React.CSSProperties = {
  background: "#ffffff",
  borderRadius: 8
};

const createBtn: React.CSSProperties = {
  background: theme.colors.primary,
  borderRadius: 9999,
  border: "none",
  padding: "0 24px",
  fontWeight: 600,
};

const emptyStateContainer: React.CSSProperties = {
  padding: "40px",
  textAlign: "center",
};

const emptyStateText: React.CSSProperties = {
  fontSize: "16px",
  color: "#666",
  marginBottom: "8px",
  fontWeight: 500,
};

const emptyStateSubtext: React.CSSProperties = {
  fontSize: "14px",
  color: "#999",
};

export default IndustryRoles;